/**
 * ATW access catalog: System → Application → Module → actions (view / create / edit / delete).
 *
 * Systems and applications are switched on with the existing `systemAccess` and `appEntitlements`
 * fields. Inside an application, each module's actions come from the person's access template
 * plus any per-person overrides, stored on their membership in that organization
 * (`accessTemplateId`, `moduleOverrides`). With no template and no overrides a person gets every
 * action, which is exactly what everyone had before module permissions existed.
 *
 * Client-safe: no server imports. Shared by ATW Platform, ATW Directory and the API guards.
 */

import type { AtwMarkId } from "@/components/Brand/atwProductMarks";
import type { AtwStoredSystemId } from "@/lib/atwSystemAccess";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";

export type AccessAction = "view" | "create" | "edit" | "delete";
export const ACCESS_ACTIONS: AccessAction[] = ["view", "create", "edit", "delete"];

export type AccessModule = {
  /** Unique within its application, e.g. `deals`. */
  key: string;
  name: string;
  /** Actions this module supports (labels can be renamed per module). */
  actions: AccessAction[];
  actionLabels?: Partial<Record<AccessAction, string>>;
  /** Settings-type module: Standard template can only view it. */
  sensitive?: boolean;
};

export type AccessApplication = {
  /** App slug inside ATW One, or the system id when the system is a single application. */
  key: string;
  name: string;
  mark?: AtwMarkId;
  /** When set, the app is switched on/off through `appEntitlements`. */
  entitlement?: Exclude<GlobalAppSlug, "home">;
  modules: AccessModule[];
};

export type AccessSystem = {
  id: AtwStoredSystemId | "directory";
  name: string;
  summary: string;
  mark: AtwMarkId;
  applications: AccessApplication[];
  /** Always granted with a Directory sign-in. Not stored on `systemAccess`. */
  pinned?: boolean;
};

const CRUD: AccessAction[] = ["view", "create", "edit", "delete"];
const VIEW_EDIT: AccessAction[] = ["view", "edit"];

export const ACCESS_CATALOG: AccessSystem[] = [
  {
    id: "directory",
    name: "ATW Directory",
    summary: "The organization roster, invites, and who can open each system.",
    mark: "directory",
    pinned: true,
    applications: [
      {
        key: "people",
        name: "People",
        mark: "directory",
        modules: [
          { key: "roster", name: "People", actions: CRUD },
          {
            key: "invites",
            name: "Invites",
            actions: ["view", "create"],
            actionLabels: { view: "View", create: "Send invites" },
          },
        ],
      },
    ],
  },
  {
    id: "one",
    name: "ATW One",
    summary: "CRM, finance, HR, marketing, knowledge, projects, analytics, support, and custom apps.",
    mark: "one",
    applications: [
      {
        key: "crm",
        name: "CRM",
        mark: "crm",
        entitlement: "crm",
        modules: [
          { key: "accounts", name: "Accounts", actions: CRUD },
          { key: "contacts", name: "Contacts", actions: CRUD },
          { key: "leads", name: "Leads", actions: CRUD },
          { key: "deals", name: "Deals", actions: CRUD },
          { key: "forms", name: "Lead forms", actions: CRUD },
          { key: "import", name: "Import", actions: ["create"], actionLabels: { create: "Run imports" } },
          { key: "growth-agent", name: "ATW Growth Agent", actions: VIEW_EDIT, sensitive: true },
        ],
      },
      {
        key: "analytics",
        name: "Analytics",
        mark: "analytics",
        entitlement: "analytics",
        modules: [
          { key: "overview", name: "Overview", actions: ["view"] },
          { key: "sites", name: "Sites", actions: CRUD },
          { key: "journeys", name: "Journeys", actions: ["view"] },
          { key: "settings", name: "Settings", actions: VIEW_EDIT, sensitive: true },
        ],
      },
      {
        key: "finance",
        name: "Finance",
        mark: "finance",
        entitlement: "finance",
        modules: [
          { key: "customers", name: "Accounts", actions: CRUD },
          { key: "items", name: "Items", actions: CRUD },
          { key: "sales", name: "Sales (estimates, invoices, payments)", actions: CRUD },
          { key: "purchases", name: "Purchases (vendors, bills)", actions: CRUD },
          { key: "settings", name: "Finance settings", actions: VIEW_EDIT, sensitive: true },
        ],
      },
      {
        key: "human-resources",
        name: "Human Resources",
        mark: "hr",
        entitlement: "human-resources",
        modules: [
          { key: "employees", name: "Employees", actions: CRUD },
          { key: "settings", name: "HR settings", actions: VIEW_EDIT, sensitive: true },
        ],
      },
      {
        key: "marketing",
        name: "Marketing",
        mark: "marketing",
        entitlement: "marketing",
        modules: [
          { key: "campaigns", name: "Campaigns", actions: CRUD },
          { key: "posts", name: "Blog posts", actions: CRUD },
          { key: "comments", name: "Comments", actions: ["view", "edit", "delete"], actionLabels: { edit: "Moderate" } },
        ],
      },
      {
        key: "knowledge",
        name: "Knowledge",
        mark: "knowledge",
        entitlement: "knowledge",
        modules: [
          { key: "articles", name: "Articles", actions: CRUD },
          { key: "collections", name: "Collections", actions: CRUD },
        ],
      },
      {
        key: "projects",
        name: "Projects",
        mark: "projects",
        entitlement: "projects",
        modules: [
          { key: "projects", name: "Projects", actions: CRUD },
          { key: "tasks", name: "Tasks", actions: CRUD },
          { key: "issues", name: "Issues", actions: CRUD },
          { key: "comments", name: "Comments", actions: ["view", "create", "delete"] },
        ],
      },
      {
        key: "support",
        name: "Support",
        mark: "support",
        entitlement: "support",
        modules: [{ key: "tickets", name: "Tickets", actions: CRUD }],
      },
      {
        key: "custom-apps",
        name: "Custom apps",
        mark: "custom-apps",
        entitlement: "custom-apps",
        modules: [{ key: "apps", name: "Custom apps", actions: CRUD }],
      },
      {
        key: "pay",
        name: "ATW Pay",
        mark: "pay",
        entitlement: "pay",
        modules: [
          { key: "payments", name: "Payments", actions: ["view", "create"], actionLabels: { create: "Take payments" } },
          { key: "api-keys", name: "API keys", actions: ["view", "create", "delete"], sensitive: true },
          { key: "checkout", name: "Checkout", actions: VIEW_EDIT },
        ],
      },
      {
        key: "worksuite",
        name: "WorkSuite",
        mark: "worksuite",
        entitlement: "worksuite",
        modules: [{ key: "integrations", name: "Integrations", actions: VIEW_EDIT, sensitive: true }],
      },
    ],
  },
  {
    id: "workspaces",
    name: "ATW WorkSuite",
    summary: "Mail, calendar, and meetings for your organization.",
    mark: "worksuite",
    applications: [
      {
        key: "mail",
        name: "Mail",
        mark: "mail",
        modules: [
          {
            key: "messages",
            name: "Mail",
            actions: ["view", "create", "edit", "delete"],
            actionLabels: { view: "Read", create: "Send", edit: "Organize", delete: "Delete" },
          },
        ],
      },
      {
        key: "calendar",
        name: "Calendar",
        mark: "mail-calendar",
        modules: [
          {
            key: "events",
            name: "Events",
            actions: CRUD,
            actionLabels: { view: "View", create: "Create", edit: "Edit", delete: "Delete" },
          },
        ],
      },
      {
        key: "meet",
        name: "Meet",
        mark: "meetings",
        modules: [
          {
            key: "rooms",
            name: "Meetings",
            actions: ["view", "create"],
            actionLabels: { view: "Join", create: "Host" },
          },
        ],
      },
    ],
  },
  {
    id: "hub",
    name: "ATW Hub",
    summary: "Team chat and collaboration.",
    mark: "hub",
    applications: [
      {
        key: "hub",
        name: "Hub",
        mark: "hub",
        modules: [
          {
            key: "messages",
            name: "Channels and messages",
            actions: CRUD,
            actionLabels: { view: "Read", create: "Post" },
          },
        ],
      },
    ],
  },
  {
    id: "agents",
    name: "ATW Agents",
    summary: "Named AI seats, Agent Configuration, Overseer, and credits.",
    mark: "agents",
    applications: [
      {
        key: "agents",
        name: "Agents",
        mark: "agents",
        modules: [
          {
            key: "seats",
            name: "Seats",
            actions: CRUD,
            actionLabels: { view: "View", create: "Hire", edit: "Configure", delete: "Archive" },
          },
          {
            key: "overseer",
            name: "Overseer",
            actions: VIEW_EDIT,
            actionLabels: { view: "View", edit: "Act on exceptions" },
          },
          {
            key: "credits",
            name: "Credits and plan",
            actions: VIEW_EDIT,
            sensitive: true,
          },
        ],
      },
    ],
  },
];

/** `crm.deals`-style key for a module. */
export function moduleId(appKey: string, moduleKey: string): string {
  return `${appKey}.${moduleKey}`;
}

export type ModuleGrant = Partial<Record<AccessAction, boolean>>;
/** Per-person changes from their template: `{ "crm.deals": { delete: false } }`. */
export type ModuleOverrides = Record<string, ModuleGrant>;

export type AccessTemplateId = "full" | "manager" | "standard" | "read_only";

export type AccessTemplate = {
  id: AccessTemplateId;
  name: string;
  summary: string;
  allows: (module: AccessModule, action: AccessAction) => boolean;
};

export const ACCESS_TEMPLATES: AccessTemplate[] = [
  {
    id: "full",
    name: "Full access",
    summary: "Every action in every module they can open. Same as before module permissions.",
    allows: () => true,
  },
  {
    id: "manager",
    name: "Manager",
    summary: "View, create and edit everywhere; delete only outside settings.",
    allows: (m, a) => a !== "delete" || !m.sensitive,
  },
  {
    id: "standard",
    name: "Standard",
    summary: "View, create and edit records; no deleting; settings are view-only.",
    allows: (m, a) => (m.sensitive ? a === "view" : a !== "delete"),
  },
  {
    id: "read_only",
    name: "Read-only",
    summary: "View only.",
    allows: (_m, a) => a === "view",
  },
];

export const DEFAULT_ACCESS_TEMPLATE_ID: AccessTemplateId = "full";

export function accessTemplate(id: string | null | undefined): AccessTemplate {
  return ACCESS_TEMPLATES.find((t) => t.id === id) ?? ACCESS_TEMPLATES[0]!;
}

export function findModule(appKey: string, moduleKey: string): AccessModule | null {
  for (const s of ACCESS_CATALOG) {
    for (const app of s.applications) {
      if (app.key !== appKey) continue;
      return app.modules.find((m) => m.key === moduleKey) ?? null;
    }
  }
  return null;
}

/** Is `action` allowed on this module by the template, after the person's overrides? */
export function moduleActionAllowed(
  templateId: string | null | undefined,
  overrides: ModuleOverrides | null | undefined,
  appKey: string,
  moduleKey: string,
  action: AccessAction,
): boolean {
  const mod = findModule(appKey, moduleKey);
  if (!mod || !mod.actions.includes(action)) return false;
  const override = overrides?.[moduleId(appKey, moduleKey)]?.[action];
  if (typeof override === "boolean") return override;
  return accessTemplate(templateId).allows(mod, action);
}

/** Drop unknown modules/actions and entries that just repeat the template. */
export function sanitizeModuleOverrides(
  templateId: string | null | undefined,
  raw: unknown,
): ModuleOverrides {
  const out: ModuleOverrides = {};
  if (!raw || typeof raw !== "object") return out;
  const template = accessTemplate(templateId);
  for (const [id, grant] of Object.entries(raw as Record<string, unknown>)) {
    const [appKey, moduleKey] = id.split(".");
    const mod = appKey && moduleKey ? findModule(appKey, moduleKey) : null;
    if (!mod || !grant || typeof grant !== "object") continue;
    const clean: ModuleGrant = {};
    for (const a of mod.actions) {
      const v = (grant as Record<string, unknown>)[a];
      if (typeof v === "boolean" && v !== template.allows(mod, a)) clean[a] = v;
    }
    if (Object.keys(clean).length) out[id] = clean;
  }
  return out;
}

export function isAccessTemplateId(v: unknown): v is AccessTemplateId {
  return typeof v === "string" && ACCESS_TEMPLATES.some((t) => t.id === v);
}
